import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, Clock, FileText, Download, MapPin, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { PageHero } from "../components/site/PageHero";
import { NewsCard } from "../components/site/Sections";
import { Field, inputCls } from "../components/forms/Field";
import { api, errMsg, fileUrl, formatDate } from "../lib/api";
import { useHumanCheck } from "../components/forms/HumanCheck";
import { NEWS_CATEGORIES } from "../data/content";

const Empty = ({ text, testId }) => <p className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid={testId}>{text}</p>;

const NewsList = () => {
  const [cat, setCat] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["news", cat], queryFn: () => api.get("/news", { params: cat ? { category: cat } : {} }).then((r) => r.data) });
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {["", ...NEWS_CATEGORIES].map((c) => (
          <button key={c || "all"} onClick={() => setCat(c)} className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${cat === c ? "border-royal bg-royal text-white" : "border-slate-200 bg-white text-slate-600 hover:border-royal"}`} data-testid={`news-filter-${c ? c.toLowerCase().replace(/\W+/g, "-") : "all"}`}>{c || "All"}</button>
        ))}
      </div>
      <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {data.map((n, i) => <NewsCard key={n.id} item={n} i={i} />)}
      </div>
      {!isLoading && !data.length && <Empty text="No updates in this category yet." testId="news-empty" />}
    </>
  );
};

const RegisterForm = ({ event }) => {
  const [f, setF] = useState({ name: "", mobile: "", email: "", employer: "" });
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");
  const human = useHumanCheck("event-reg");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post(`/events/${event.id}/register`, { ...f, email: f.email || null, ...human.payload });
      setRef(data.ref_id);
    } catch (err) { toast.error(errMsg(err)); human.handleError(err); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  if (ref) return <div className="py-4 text-center" data-testid="event-register-success"><p className="text-sm text-slate-600">You are registered. Reference number:</p><p className="mt-2 font-mono text-xl font-bold text-royal">{ref}</p></div>;
  return (
    <form onSubmit={submit} className="space-y-4">
      <Field label="Full Name" required><Input className={inputCls} required value={f.name} onChange={set("name")} data-testid="event-reg-name" /></Field>
      <Field label="Mobile Number" required><Input className={inputCls} required value={f.mobile} onChange={set("mobile")} data-testid="event-reg-mobile" /></Field>
      <Field label="Email"><Input className={inputCls} type="email" value={f.email} onChange={set("email")} data-testid="event-reg-email" /></Field>
      <Field label="Company / Employer"><Input className={inputCls} value={f.employer} onChange={set("employer")} data-testid="event-reg-employer" /></Field>
      {human.ui}
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="event-reg-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Confirm Registration</button>
    </form>
  );
};

const RegisterDialog = ({ event, onClose }) => (
  <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="event-register-dialog">
      <DialogHeader><DialogTitle className="font-display text-navy">Register: {event?.title}</DialogTitle></DialogHeader>
      {event && <RegisterForm key={event.id} event={event} />}
    </DialogContent>
  </Dialog>
);

const EventsList = () => {
  const { data = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const [sel, setSel] = useState(null);
  if (!isLoading && !data.length) return <Empty text="Upcoming events and programmes will be announced here." testId="events-empty" />;
  return (
    <div className="grid gap-5 md:grid-cols-2">
      {data.map((e, i) => (
        <div key={e.id} className="flex gap-5 rounded-xl border border-slate-200 bg-white p-6" data-testid={`event-card-${i}`}>
          <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-lg bg-navy text-white">
            <span className="text-2xl font-extrabold font-display">{new Date(e.date).getDate() || "--"}</span>
            <span className="text-[11px] uppercase tracking-wider text-emerald-300">{new Date(e.date).toLocaleString("en-IN", { month: "short" })}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold text-navy">{e.title}</h3>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{formatDate(e.date)}</span>
              {e.time && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{e.time}</span>}
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{e.venue}</span>
            </div>
            {e.description && <p className="mt-3 text-sm text-slate-600">{e.description}</p>}
            {e.registration_open ? (
              <button onClick={() => setSel(e)} className="btn-green mt-4 !px-4 !py-2" data-testid={`event-register-button-${i}`}>Register</button>
            ) : <p className="mt-4 text-xs font-semibold text-slate-400">Registration closed</p>}
          </div>
        </div>
      ))}
      <RegisterDialog event={sel} onClose={() => setSel(null)} />
    </div>
  );
};

const Downloads = () => {
  const { data = [], isLoading } = useQuery({ queryKey: ["documents"], queryFn: () => api.get("/documents").then((r) => r.data) });
  const printable = [{ id: "form", title: "Membership Application Form", description: "Printable form for offline submission at the federation office.", href: "/membership-form" }];
  const docs = printable.concat(data.map((d) => ({ ...d, href: fileUrl(d.file_id) })));
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {docs.map((d, i) => (
        <a key={d.id} href={d.href} target="_blank" rel="noreferrer" className="card-lift flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={`document-${i}`}>
          <FileText className="h-9 w-9 shrink-0 text-royal" />
          <div className="min-w-0 flex-1"><p className="font-semibold text-navy">{d.title}</p><p className="truncate text-xs text-slate-500">{d.description}</p></div>
          <Download className="h-5 w-5 text-slate-400" />
        </a>
      ))}
      {!isLoading && !data.length && <p className="self-center text-sm text-slate-500">More documents will be published here.</p>}
    </div>
  );
};

export default function News() {
  const [params, setParams] = useSearchParams();
  const tab = ["events", "downloads"].includes(params.get("tab")) ? params.get("tab") : "news";
  return (
    <>
      <PageHero eyebrow="News & Activities" title="News & Activities" subtitle="Announcements, awareness programmes, union activities, meetings and policy updates from the federation." image="/images/kozhikode-boat.jpg" testId="news-hero" />
      <section className="py-16 md:py-20">
        <div className="container-x">
          <Tabs value={tab} onValueChange={(v) => setParams(v === "news" ? {} : { tab: v })}>
            <TabsList className="mb-8 h-12 bg-white p-1 shadow-sm">
              <TabsTrigger value="news" className="h-10 px-5 data-[state=active]:bg-royal data-[state=active]:text-white" data-testid="tab-news">News</TabsTrigger>
              <TabsTrigger value="events" className="h-10 px-5 data-[state=active]:bg-royal data-[state=active]:text-white" data-testid="tab-events">Events</TabsTrigger>
              <TabsTrigger value="downloads" className="h-10 px-5 data-[state=active]:bg-royal data-[state=active]:text-white" data-testid="tab-downloads">Downloads</TabsTrigger>
            </TabsList>
            <TabsContent value="news"><NewsList /></TabsContent>
            <TabsContent value="events"><EventsList /></TabsContent>
            <TabsContent value="downloads"><Downloads /></TabsContent>
          </Tabs>
        </div>
      </section>
    </>
  );
}
